import { Flex } from "antd";
import { useEffect, useRef, RefObject } from "react";
import { DEFAULT_COLOR } from "../../storyboardEditor/constants";
import { ColoredBackground } from "../../storyboardEditor/components/Template/Preview/components/ColoredBackground";
import { useTemplateEditorStore } from "../../storyboardEditor/store/useTemplateEditorStore";
import { useTemplateData } from "../../storyboardEditor/hooks/useTemplateData";
import { TemplateShapes } from "../../storyboardEditor/components/Template/Preview/components/TemplateShapes";
import { TemplateImages } from "../../storyboardEditor/components/Template/Preview/components/TemplateImages";
import { TemplateVideos } from "../../storyboardEditor/components/Template/Preview/components/TemplateVideos";
import { TemplateTable } from "../../storyboardEditor/components/Template/Preview/components/TemplateTable";
import { EditableLogo } from "../../storyboardEditor/components/Template/Preview/components/EditableLogo";
import { Presenter } from "../../storyboardEditor/components/Template/Preview/components/Presenter";
import { TemplateMainImage } from "../../storyboardEditor/components/Template/Preview/components/TemplateMainImage";
import { TemplateParameteredTexts } from "../../storyboardEditor/components/Template/Preview/components/TemplateParameteredTexts";
import {
  StyledWrapper,
  StyledPreview,
} from "../../storyboardEditor/components/Template/SlideEditableTemplate";
import { Slide } from "pages/media/types/";
import { MoveableRegistryProvider } from "../../storyboardEditor/components/Template/Preview/moveableRegistry";
import { MoveableLayer } from "../../storyboardEditor/components/Template/Preview/components/MoveableLayer";

type ThemeSlideEditableTemplateProps = {
  previewWidth: number;
  previewHeight: number;
  templateScrollProps: {
    settingsToolbarRef?: RefObject<HTMLDivElement>;
  };
  slide: Slide;
};

export const ThemeSlideEditableTemplate = ({
  previewWidth,
  previewHeight,
  templateScrollProps,
  slide,
}: ThemeSlideEditableTemplateProps) => {
  const {
    mediaGenerationSettingsData: { mediaAspectRatio },
  } = useTemplateData();

  const { templateState } = useTemplateEditorStore((store) => ({
    templateState: store.templateState,
  }));

  const bulletPoints =
    "bullet_points" in slide.placeholders
      ? slide.placeholders.bullet_points
      : undefined;

  const {
    presenter,
    parameteredTexts,
    figure,
    slideLayoutShapes,
    background,
    shapes,
    table,
    mainImage,
    images,
    videos,
  } = templateState;

  const templateRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const templateElement = templateRef.current;
    if (templateElement && templateScrollProps.settingsToolbarRef) {
      templateElement.style.setProperty(
        "scroll-margin-block-start",
        `${templateScrollProps.settingsToolbarRef.current?.getBoundingClientRect().bottom ?? 0}px`
      );
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          templateElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      });
    }
  }, [templateScrollProps.settingsToolbarRef, mediaAspectRatio]);

  return (
    <Flex ref={templateRef} vertical justify="center" align="center" gap={8}>
      <StyledWrapper
        className="template-preview"
        data-testid="template-preview"
      >
        <StyledPreview
          ref={previewRef}
          data-interactive
          width={previewWidth}
          height={previewHeight}
          $backgroundColor={DEFAULT_COLOR}
        >
          <MoveableRegistryProvider>
            <ColoredBackground
              width={previewWidth}
              height={previewHeight}
              backgroundColor={background.bodyColor}
            />
            <EditableLogo
              previewWidth={previewWidth}
              previewHeight={previewHeight}
            />
            {presenter.presenter && (
              <Presenter
                draggable={true}
                presenter={presenter}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
              />
            )}
            <TemplateMainImage
              previewWidth={previewWidth}
              previewHeight={previewHeight}
              slide={slide}
              mainImage={mainImage}
              editable={true}
              saveRatio={false}
            />
            <TemplateParameteredTexts
              previewWidth={previewWidth}
              previewHeight={previewHeight}
              texts={parameteredTexts ?? []}
              figure={figure}
              editable
            />
            <TemplateImages
              templateImages={images}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
              editable
            />
            <TemplateVideos
              templateVideos={videos}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
              editable
            />
            <TemplateShapes
              shapes={[...(slideLayoutShapes ?? []), ...shapes]}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
              editable
            />
            {table && bulletPoints && (
              <TemplateTable
                table={table}
                bulletPoints={bulletPoints}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                editable
              />
            )}
            <MoveableLayer
              previewWidth={previewWidth}
              previewHeight={previewHeight}
              rootRef={previewRef}
            />
          </MoveableRegistryProvider>
        </StyledPreview>
      </StyledWrapper>
    </Flex>
  );
};
